import React from 'react';
import { LayoutDashboard, CreditCard, Settings, MessageSquare, LogOut, Users, Wrench } from 'lucide-react';
import sixthLogo from '../../../assets/sixth_logo_white.png';
import { cn } from '../../../lib/utils';
import PlanSwitcher from '../PlanSwitcher';

const Sidebar = ({ activeTab, onTabChange, userInfo, userType, onLogout, planType }) => {
  const isTeamAdmin = userType === 'team_admin';
  const isTeamMember = userType === 'team_member';
  const isTeamUser = isTeamAdmin || isTeamMember;

  // Team navigation items
  const teamNavigationItems = [
    {
      id: 'team-overview',
      label: 'Team Dashboard',
      icon: Users
    },
    ...(isTeamAdmin ? [{
      id: 'team-settings',
      label: 'Team Settings',
      icon: Wrench
    }] : [])
  ];

  // Personal navigation items
  const personalNavigationItems = [
    {
      id: 'overview',
      label: 'Overview',
      icon: LayoutDashboard
    },
    {
      id: 'billing',
      label: 'Billing',
      icon: CreditCard
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings
    },
    {
      id: 'contact',
      label: 'Contact Support',
      icon: MessageSquare
    }
  ];

  const getUserInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="w-[280px] h-screen bg-[#121219]/95 backdrop-blur-xl border-r border-white/10 flex flex-col fixed left-0 top-0 z-50">
      <div className="p-6 border-b border-white/10 bg-white/5">
        <div className="flex items-center justify-start">
          <img src={sixthLogo} alt="Sixth Logo" className="h-8" />
        </div>
      </div>

      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white font-bold shadow-lg shadow-primary/20">
            {getUserInitials(userInfo?.fullname)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white font-medium truncate">{userInfo?.fullname || 'User'}</div>
            <div className="text-xs text-muted-foreground truncate">{userInfo?.email || 'user@example.com'}</div>
            {isTeamUser && (
              <span className={cn(
                "inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase",
                isTeamAdmin
                  ? "bg-primary/10 text-primary"
                  : "bg-purple-500/10 text-purple-400"
              )}>
                {isTeamAdmin ? 'Team Admin' : 'Team Member'}
              </span>
            )}
          </div>
        </div>
      </div>

      <nav className="flex-1 py-6 px-4 overflow-y-auto">
        {/* Team Navigation */}
        {isTeamUser && (
          <div className="mb-4">
            <div className="px-3 mb-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Team</div>
            <div className="space-y-1">
              {teamNavigationItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => onTabChange(item.id)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
                    activeTab === item.id
                      ? "bg-primary/10 text-white shadow-sm shadow-primary/5"
                      : "text-muted-foreground hover:text-white hover:bg-white/5"
                  )}
                >
                  <item.icon className={cn("w-4 h-4", activeTab === item.id ? "text-primary" : "text-muted-foreground")} />
                  {item.label}
                </button>
              ))}
            </div>
            <div className="my-3 mx-3 h-px bg-white/10" />
          </div>
        )}

        {/* Personal Navigation */}
        {isTeamUser && (
          <div className="px-3 mb-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Personal</div>
        )}
        <div className="space-y-1">
          {personalNavigationItems.map((item) => (
            <button
              key={item.id}
              onClick={() => onTabChange(item.id)}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
                activeTab === item.id
                  ? "bg-primary/10 text-white shadow-sm shadow-primary/5"
                  : "text-muted-foreground hover:text-white hover:bg-white/5"
              )}
            >
              <item.icon className={cn("w-4 h-4", activeTab === item.id ? "text-primary" : "text-muted-foreground")} />
              {item.label}
            </button>
          ))}
        </div>
      </nav>

      {/* Plan Switcher */}
      <div className="p-4 border-t border-white/10">
        <PlanSwitcher currentPlan={planType || 'fixed'} />
      </div>

      <div className="p-4 border-t border-white/10">
        <button
          onClick={onLogout}
          className="w-full flex items-center justify-center gap-2 p-3 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 hover:text-red-300 transition-all font-medium text-sm"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
